<template>
  <div>
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <v-overlay :value="loading" absolute>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <div v-if="docs" class="print-content">
        <!-- Basic Information -->
        <v-row dense>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="收件日期" rules="required">
              <DateInputSeparate
                v-model="payDate"
                label="收件日期 *"
                :error="!!errors.length"
                :error-messages="errors"
                dense
                flat
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="車牌號碼" rules="required">
              <v-text-field
                v-model="docs.vehno"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="車牌號碼 *"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <SearchableCombobox
              v-model="docs.customer"
              path="v1/autocomplete-profiles"
              label="客戶"
              :search-paths="['name', 'phone']"
              :params="{ filter: { type: 'client' }, searchPaths: ['name'] }"
              item-text="name"
              dense
              @selectItem="handleCustomerSelect"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ item.phone }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </SearchableCombobox>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <v-text-field
              v-model="docs.customerTel"
              label="電話"
              type="tel"
              autocomplete="off"
              outlined
              dense
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6" class="d-flex align-center">
            <v-checkbox
              v-model="docs.isTel"
              label="已透過運輸署網頁核實"
              hide-details
              dense
              class="mt-0"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <SearchableCombobox
              v-model="docs.agent"
              path="v1/autocomplete-profiles"
              label="代理人"
              :params="{ filter: { type: 'agent' }, searchPaths: ['name'] }"
              item-text="name"
              dense
              @selectItem="handleAgentSelect"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ item.phone }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </SearchableCombobox>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <SearchableCombobox
              v-model="docs.agentTel"
              path="v1/autocomplete-profiles"
              label="代理人電話"
              :params="{ filter: { type: 'agent' }, searchPaths: ['phone'] }"
              item-text="phone"
              dense
              @selectItem="handleAgentPhoneSelect"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.phone }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ item.name }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </SearchableCombobox>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6" class="d-flex align-center">
            <v-checkbox v-model="docs.isJj" label="緊急辦理" hide-details dense class="mt-0" />
          </v-col>
        </v-row>

        <!-- Services Section -->
        <v-subheader class="px-0 mt-4">代辦服務</v-subheader>
        <v-row dense>
          <v-col cols="12">
            <v-card outlined>
              <v-card-text>
                <v-row dense>
                  <!-- Service Selection List -->
                  <v-col cols="12" md="4">
                    <div
                      style="
                        max-height: 400px;
                        overflow-y: auto;
                        border: 1px solid #e0e0e0;
                        padding: 8px;
                        border-radius: 4px;
                      "
                    >
                      <div v-for="category in serviceCategories" :key="category.type">
                        <v-subheader
                          v-if="category.label"
                          class="px-0 subtitle-2 font-weight-bold"
                          >{{ category.label }}</v-subheader
                        >
                        <v-list dense class="pa-0">
                          <v-list-item
                            v-for="service in category.services"
                            :key="service"
                            @click="addService(service)"
                            style="cursor: pointer"
                            class="px-2"
                          >
                            <v-list-item-content>
                              <v-list-item-title
                                :class="{ 'red--text': isServiceSelected(service) }"
                              >
                                {{ service }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </div>
                    </div>
                  </v-col>

                  <!-- Selected Services with Prices -->
                  <v-col cols="12" md="8">
                    <v-simple-table dense>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left">代辦服務</th>
                            <th class="text-left" width="150">有關機構收費</th>
                            <th class="text-left" width="150">佑安手續費</th>
                            <th class="text-center" width="100">操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(service, index) in docs.services" :key="'service-' + index">
                            <td>{{ service.name }}</td>
                            <td>
                              <v-text-field
                                v-model.number="service.price1"
                                type="number"
                                dense
                                hide-details
                                outlined
                                style="width: 120px"
                              />
                            </td>
                            <td>
                              <v-text-field
                                v-model.number="service.price2"
                                type="number"
                                dense
                                hide-details
                                outlined
                                style="width: 120px"
                              />
                            </td>
                            <td class="text-center">
                              <v-btn icon small color="error" @click="removeService(index)">
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                          <tr v-if="docs.services.length === 0">
                            <td colspan="4" class="text-center grey--text">尚未添加代辦服務</td>
                          </tr>
                          <!-- Totals Row -->
                          <tr v-if="docs.services.length > 0" class="font-weight-bold">
                            <td>小計</td>
                            <td>{{ totalPrice1 }}</td>
                            <td>{{ totalPrice2 }}</td>
                            <td></td>
                          </tr>
                          <tr
                            v-if="docs.services.length > 0"
                            class="font-weight-bold primary--text"
                          >
                            <td>總計</td>
                            <td></td>
                            <td colspan="2">{{ totalPrice }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Payment Section -->
        <v-subheader class="px-0 mt-4">付款資訊</v-subheader>
        <v-row dense>
          <v-col cols="12">
            <v-card outlined>
              <v-card-text>
                <v-row dense>
                  <!-- Payment Methods Selection List -->
                  <v-col cols="12" md="3">
                    <div style="border: 1px solid #e0e0e0; padding: 8px; border-radius: 4px">
                      <v-subheader class="px-0 subtitle-2">付款方式</v-subheader>
                      <v-list dense>
                        <v-list-item
                          v-for="method in paymentMethodOptions"
                          :key="method.value"
                          @click="togglePaymentMethod(method.value)"
                          style="cursor: pointer"
                        >
                          <v-list-item-content>
                            <v-list-item-title>{{ method.text }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </div>
                  </v-col>

                  <!-- Selected Payment Methods -->
                  <v-col cols="12" md="2">
                    <v-subheader class="px-0 subtitle-2">已選擇</v-subheader>
                    <v-chip-group column>
                      <v-chip
                        v-for="method in docs.payment.paymentMethods"
                        :key="method"
                        close
                        @click:close="togglePaymentMethod(method)"
                      >
                        {{ method }}
                      </v-chip>
                    </v-chip-group>
                    <div
                      v-if="docs.payment.paymentMethods.length === 0"
                      class="grey--text text-caption"
                    >
                      尚未選擇付款方式
                    </div>
                  </v-col>

                  <!-- Payment Status -->
                  <v-col cols="12" md="4">
                    <v-subheader class="px-0 subtitle-2">付款狀態</v-subheader>
                    <v-radio-group v-model="docs.payment.paid" hide-details dense>
                      <v-radio :value="false" label="尚未付款" />
                      <v-radio :value="true">
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-text-field
                              v-if="docs.payment.paid"
                              v-model="docs.payment.paidDate"
                              hide-details
                              dense
                              outlined
                              class="mr-1"
                            />
                            <span>已付款</span>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </v-col>

                  <!-- Payment Remarks -->
                  <v-col cols="12" md="3">
                    <v-subheader class="px-0 subtitle-2">備註選項</v-subheader>
                    <div v-for="remarkOption in paymentRemarkOptions" :key="remarkOption.key">
                      <v-checkbox
                        v-model="docs.payment.remarks[remarkOption.key].selected"
                        :label="remarkOption.label"
                        hide-details
                        dense
                        class="mt-2"
                      />
                      <v-text-field
                        v-model="docs.payment.remarks[remarkOption.key].detail"
                        dense
                        outlined
                        hide-details
                        class="mt-1 mb-2"
                        placeholder="請輸入詳情"
                      />
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Others List Section -->
        <v-subheader class="px-0 mt-4">待補文件</v-subheader>
        <v-row dense>
          <v-col cols="12">
            <v-card outlined>
              <v-card-text>
                <v-row dense>
                  <!-- Others Selection List -->
                  <v-col cols="12" md="6">
                    <div style="border: 1px solid #e0e0e0; padding: 8px; border-radius: 4px">
                      <v-list dense>
                        <v-list-item
                          v-for="option in othersListOptions"
                          :key="option.value"
                          @click="toggleOthersList(option.value)"
                          style="cursor: pointer"
                        >
                          <v-list-item-content>
                            <v-list-item-title>{{ option.text }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </div>
                  </v-col>

                  <!-- Selected Others -->
                  <v-col cols="12" md="6">
                    <v-subheader class="px-0 subtitle-2">已選擇的文件</v-subheader>
                    <v-chip-group column>
                      <v-chip
                        v-for="item in docs.othersList"
                        :key="item"
                        close
                        @click:close="toggleOthersList(item)"
                      >
                        {{ item }}
                      </v-chip>
                    </v-chip-group>
                    <div v-if="docs.othersList.length === 0" class="grey--text text-caption">
                      尚未選擇待補文件
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Remarks -->
        <v-row dense>
          <v-col cols="12">
            <v-textarea
              v-model="docs.remark2"
              flat
              label="備註"
              outlined
              rows="3"
              dense
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <!-- Additional Dates -->
        <v-row dense>
          <v-col cols="12" md="auto">
            <DateInputSeparate
              v-model="finishDate"
              label="到期日"
              dense
              flat
              hide-details="auto"
              @keydown.enter.native="save"
            />
          </v-col>
          <v-col cols="12" md="auto">
            <DateInputSeparate
              v-model="inputDate"
              label="辦理日期"
              dense
              flat
              hide-details="auto"
              @keydown.enter.native="save"
            />
          </v-col>
        </v-row>

        <!-- Attachments Section -->
        <v-row dense>
          <v-col cols="12">
            <div class="d-flex align-center justify-space-between mb-3">
              <label class="v-label">附件檔案</label>
              <v-btn small color="primary" @click="$refs.attachmentInput.click()">
                <v-icon small left>mdi-upload</v-icon>
                上傳檔案
              </v-btn>
            </div>

            <!-- Attachments Table -->
            <v-simple-table v-if="docs.attachments && docs.attachments.length > 0" dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">檔案名稱</th>
                    <th class="text-center" width="100">查看</th>
                    <th class="text-center" width="100">刪除</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(attachment, index) in docs.attachments" :key="'attachment-' + index">
                    <td>
                      <v-icon small left>mdi-file-document</v-icon>
                      {{ attachment.filename || '文件' }}
                    </td>
                    <td class="text-center">
                      <v-btn icon small @click="downloadAttachment(attachment)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </td>
                    <td class="text-center">
                      <v-btn icon small color="error" @click="removeAttachment(index)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-alert v-else type="info" outlined dense class="mt-2"> 尚未上傳任何附件 </v-alert>

            <!-- Hidden file input -->
            <input
              ref="attachmentInput"
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              style="display: none"
              @change="handleAttachmentSelect"
              multiple
            />
          </v-col>
        </v-row>

        <!-- Action Buttons -->
        <v-row dense class="mt-4 no-print">
          <v-col class="d-flex align-center gap-2">
            <v-btn
              :disabled="invalid || isMaster || saving"
              :loading="saving"
              color="primary"
              @click="save"
            >
              立即提交
            </v-btn>
            <v-spacer />

            <v-btn :disabled="saving" color="default" outlined @click="resetForm">重新填寫</v-btn>

            <v-btn
              v-if="!isNewRecord"
              :disabled="saving"
              color="error"
              outlined
              @click="confirmDelete"
            >
              刪除
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </ValidationObserver>

    <!-- Attachment Preview Dialog -->
    <AttachmentPreviewDialog
      v-model="previewDialog"
      :attachment-id="previewAttachmentId"
      :filename="previewFilename"
      :content-type="previewContentType"
    />
  </div>
</template>

<script>
import api from '@/api';
import { crudRead, crudUpdate } from '@/api/crud';
import { mapGetters } from 'vuex';
import DateInputSeparate from '@/components/DateInputSeparate';
import AttachmentPreviewDialog from '@/components/AttachmentPreviewDialog';
import SearchableCombobox from '@/components/SearchableCombobox';
import { RESTFUL, yomtOptionsSubTypes } from '@/data/constants';
import {
  yomtPaymentMethodOptions,
  yomtOthersListOptions,
  yomtPaymentRemarkOptions,
} from '@/data/optionsConstants';
import dayjs from 'dayjs';
import ApiError from '@/api/ApiError';
import { uploadSingleAttachment } from '@/api/attachment';
import { getOptions } from '@/api/options';

export default {
  name: 'YOMTDetailsForm',
  components: {
    DateInputSeparate,
    AttachmentPreviewDialog,
    SearchableCombobox,
  },
  props: {
    id: {
      type: String,
      default: 'new',
    },
    initialData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      docs: null,
      paymentMethodOptions: yomtPaymentMethodOptions,
      othersListOptions: yomtOthersListOptions,
      paymentRemarkOptions: yomtPaymentRemarkOptions,
      serviceCategories: [],
      newAttachments: [],
      loading: false,
      saving: false,
      previewDialog: false,
      previewAttachmentId: null,
      previewFilename: '',
      previewContentType: '',
    };
  },
  async mounted() {
    await this.loadServiceOptions();
    this.reload();
  },
  watch: {
    'docs.payment.paid'(newValue) {
      if (newValue && !this.docs.payment.paidDate) {
        this.docs.payment.paidDate = dayjs().format('YYYY-MM-DD');
      } else if (!newValue) {
        this.docs.payment.paidDate = null;
      }
    },
  },
  computed: {
    ...mapGetters('auth', ['role', 'isMaster', 'profileId']),
    isNewRecord() {
      return !this.id || this.id === 'new';
    },
    payDate: {
      get() {
        if (!this.docs?.payDate) return null;
        return dayjs(this.docs.payDate).format('YYYY-MM-DD');
      },
      set(value) {
        if (!value) {
          this.docs.payDate = null;
          return;
        }
        this.docs.payDate = dayjs(value, 'YYYY-MM-DD').startOf('day').toDate();
      },
    },
    inputDate: {
      get() {
        if (!this.docs?.inputDate) return null;
        return dayjs(this.docs.inputDate).format('YYYY-MM-DD');
      },
      set(value) {
        if (!value) {
          this.docs.inputDate = null;
          return;
        }
        this.docs.inputDate = dayjs(value, 'YYYY-MM-DD').startOf('day').toDate();
      },
    },
    finishDate: {
      get() {
        if (!this.docs?.finishDate) return null;
        return dayjs(this.docs.finishDate).format('YYYY-MM-DD');
      },
      set(value) {
        if (!value) {
          this.docs.finishDate = null;
          return;
        }
        this.docs.finishDate = dayjs(value, 'YYYY-MM-DD').startOf('day').toDate();
      },
    },
    totalPrice1() {
      return this.docs.services.reduce((sum, service) => sum + (service.price1 || 0), 0);
    },
    totalPrice2() {
      return this.docs.services.reduce((sum, service) => sum + (service.price2 || 0), 0);
    },
    totalPrice() {
      return this.totalPrice1 + this.totalPrice2;
    },
  },
  methods: {
    async loadServiceOptions() {
      this.loading = true;
      try {
        // Load all service options from API
        const allOptions = await getOptions(api, {
          filter: { type: 'yomt' },
          sort: { subTypeOrder: 1, order: 1, createdAt: -1 },
        });

        const options = allOptions.docs || allOptions;

        // Get unique subTypes with their subTypeOrder (including empty subType)
        const subTypeMap = new Map();
        options.forEach(opt => {
          const subType = opt.subType || '';
          if (!subTypeMap.has(subType)) {
            subTypeMap.set(subType, opt.subTypeOrder || 0);
          }
        });

        // Sort subTypes by subTypeOrder
        const subTypes = Array.from(subTypeMap.entries())
          .sort((a, b) => a[1] - b[1])
          .map(entry => entry[0]);

        // Build service categories
        this.serviceCategories = [];

        // Add all subTypes in sorted order
        subTypes.forEach(subType => {
          const services = options
            .filter(opt => (opt.subType || '') === subType)
            .map(opt => opt.name);

          if (services.length > 0) {
            this.serviceCategories.push({
              type: subType,
              label: subType,
              services: services,
            });
          }
        });
      } catch (e) {
        console.error('Failed to load service options:', e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '載入服務選項失敗',
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    async reload() {
      if (this.isNewRecord) {
        // Initialize new record with default values
        const now = dayjs().startOf('day');

        if (this.initialData) {
          // Use provided initial data for duplication
          this.docs = {
            payDate: now.toDate(),
            inputDate: null,
            finishDate: null,
            vehno: this.initialData.vehno || '',
            customer: this.initialData.customer || '',
            customerTel: this.initialData.customerTel || '',
            agent: this.initialData.agent || '',
            agentTel: this.initialData.agentTel || '',
            isJj: this.initialData.isJj || false,
            isTel: this.initialData.isTel || false,
            services: [],
            remark: '',
            remark2: '',
            othersList: [],
            payment: {
              paymentMethods: [],
              paid: false,
              paidDate: null,
              remarks: {
                vehicle_reassignment_registration_number: { selected: false, detail: '' },
                sold_plate_number: { selected: false, detail: '' },
                cheque_details: { selected: false, detail: '' },
                deposit_paid: { selected: false, detail: '' },
              },
            },
            attachments: [],
          };
        } else {
          // Default new record
          this.docs = {
            payDate: now.toDate(),
            inputDate: null,
            finishDate: null,
            vehno: '',
            customer: '',
            customerTel: '',
            agent: '',
            agentTel: '',
            isJj: false,
            isTel: false,
            services: [],
            remark: '',
            remark2: '',
            othersList: [],
            payment: {
              paymentMethods: [],
              paid: false,
              paidDate: null,
              remarks: {
                vehicle_reassignment_registration_number: { selected: false, detail: '' },
                sold_plate_number: { selected: false, detail: '' },
                cheque_details: { selected: false, detail: '' },
                deposit_paid: { selected: false, detail: '' },
              },
            },
            attachments: [],
          };
        }
      } else {
        this.docs = await crudRead(api, RESTFUL.yomt.read, this.id, {
          populates: [{ path: 'attachments', select: ['filename', '_id', 'contentType'] }],
        });

        // Ensure nested structures exist
        if (!this.docs.services) {
          this.docs.services = [];
        }
        if (!this.docs.othersList) {
          this.docs.othersList = [];
        }
        if (!this.docs.attachments) {
          this.docs.attachments = [];
        }
        if (!this.docs.payment) {
          this.docs.payment = {
            paymentMethods: [],
            paid: false,
            paidDate: null,
            remarks: {
              vehicle_reassignment_registration_number: { selected: false, detail: '' },
              sold_plate_number: { selected: false, detail: '' },
              cheque_details: { selected: false, detail: '' },
              deposit_paid: { selected: false, detail: '' },
            },
          };
        }
        if (!this.docs.payment.remarks) {
          this.docs.payment.remarks = {
            vehicle_reassignment_registration_number: { selected: false, detail: '' },
            sold_plate_number: { selected: false, detail: '' },
            cheque_details: { selected: false, detail: '' },
            deposit_paid: { selected: false, detail: '' },
          };
        }

        // Load attachment details
      }
    },
    isServiceSelected(serviceName) {
      return this.docs.services.some(service => service.name === serviceName);
    },
    addService(serviceName) {
      // Check if service is already selected
      const existingIndex = this.docs.services.findIndex(service => service.name === serviceName);

      if (existingIndex > -1) {
        // Service already selected, remove it
        this.docs.services.splice(existingIndex, 1);
      } else {
        // Service not selected, add it
        this.docs.services.push({
          name: serviceName,
          price1: 0,
          price2: 0,
        });
      }
    },
    removeService(index) {
      this.docs.services.splice(index, 1);
    },
    togglePaymentMethod(method) {
      const index = this.docs.payment.paymentMethods.indexOf(method);
      if (index > -1) {
        // Removing an existing payment method
        this.docs.payment.paymentMethods.splice(index, 1);
        // If no payment methods remain, mark as unpaid and clear paidDate
        if (this.docs.payment.paymentMethods.length === 0) {
          this.docs.payment.paid = false;
          this.docs.payment.paidDate = null;
        }
      } else {
        // Adding a new payment method
        this.docs.payment.paymentMethods.push(method);
        // Automatically mark as paid and set today's date
        this.docs.payment.paid = true;
        this.docs.payment.paidDate = dayjs().format('YYYY-MM-DD');
      }
    },
    toggleOthersList(item) {
      const index = this.docs.othersList.indexOf(item);
      if (index > -1) {
        this.docs.othersList.splice(index, 1);
      } else {
        this.docs.othersList.push(item);
      }
    },
    handleCustomerSelect(customer) {
      // Only update related phone field when user clicks an item from dropdown
      if (customer && typeof customer === 'object') {
        this.docs.customer = customer.name || '';
        this.docs.customerTel = customer.phone || '';
      }
    },
    handleAgentSelect(agent) {
      // Only update related phone field when user clicks an item from dropdown
      if (agent && typeof agent === 'object') {
        this.docs.agent = agent.name || '';
        this.docs.agentTel = agent.phone || '';
      }
    },
    handleAgentPhoneSelect(profile) {
      // Only update related name field when user clicks an item from dropdown
      if (profile && typeof profile === 'object') {
        this.docs.agentTel = profile.phone || '';
        this.docs.agent = profile.name || '';
      }
    },
    async handleAttachmentSelect(event) {
      const files = Array.from(event.target.files);
      if (files.length) {
        this.newAttachments = files;
        await this.uploadAttachments();
        event.target.value = ''; // Reset input
      }
    },
    async uploadAttachments() {
      if (!this.newAttachments.length) {
        return;
      }

      try {
        const attachments = await Promise.all(
          this.newAttachments.map(file => uploadSingleAttachment(api, file, this.profileId)),
        );

        // Add to the attachments list
        const currentAttachments = Array.isArray(this.docs.attachments)
          ? this.docs.attachments
          : [];

        this.docs.attachments = [...currentAttachments, ...attachments];
        this.newAttachments = [];

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '附件已添加，請點擊儲存以保存變更',
          type: 'info',
          color: 'info',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    async removeAttachment(index) {
      const confirmed = await this.$root.$confirm.open('確認刪除', '確定要刪除此附件嗎？', {
        color: 'red',
      });
      if (!confirmed) {
        return;
      }

      try {
        // Just remove from the list, will be saved when user clicks save
        this.docs.attachments.splice(index, 1);

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '附件已移除，請點擊儲存以保存變更',
          type: 'info',
          color: 'info',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    downloadAttachment(attachment) {
      const attachmentId = typeof attachment === 'object' ? attachment._id : attachment;
      const filename = typeof attachment === 'object' ? attachment.filename : '';
      if (attachmentId) {
        // Open all files in viewer page (new tab)
        const route = this.$router.resolve({
          name: 'FileViewerPage',
          params: { attachmentId },
          query: { filename: filename || '文件' },
        });
        window.open(route.href, '_blank');
      }
    },
    resetForm() {
      // Reset form validation
      this.$refs.form?.reset();
      // Reload data
      this.reload();
    },

    async confirmDelete() {
      const confirmed = await this.$root.$confirm.open(
        '確認刪除',
        '確定要刪除此記錄嗎？此操作無法復原。',
        {
          color: 'red',
        },
      );
      if (!confirmed) {
        return;
      }
      this.deleteRecord();
    },
    async deleteRecord() {
      try {
        await api.delete(`${RESTFUL.yomt.read}/${this.id}`);

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '刪除成功',
          type: 'success',
          color: 'success',
        });

        // Emit deleted event
        this.$emit('deleted');
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    async save() {
      this.saving = true;
      try {
        if (!(await this.$refs.form?.validate?.())) {
          this.saving = false;
          return;
        }

        // Prepare payload
        const payload = {
          payDate: this.docs.payDate,
          inputDate: this.docs.inputDate,
          finishDate: this.docs.finishDate,
          vehno: this.docs.vehno,
          customer: this.docs.customer,
          customerTel: this.docs.customerTel,
          agent: this.docs.agent,
          agentTel: this.docs.agentTel,
          isJj: this.docs.isJj,
          isTel: this.docs.isTel,
          services: this.docs.services,
          remark: this.docs.remark,
          remark2: this.docs.remark2,
          othersList: this.docs.othersList,
          payment: this.docs.payment,
          attachments: this.docs.attachments
            ? this.docs.attachments.map(att => (typeof att === 'object' ? att._id : att))
            : [],
        };

        if (this.isNewRecord) {
          await api.post(RESTFUL.yomt.create, payload);
        } else {
          await crudUpdate(api, RESTFUL.yomt.read, this.id, payload);
        }

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '儲存成功',
          type: 'success',
          color: 'success',
        });

        // Emit saved event
        this.$emit('saved');
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
